import { useState } from 'react';
import { ChevronDown, ChevronUp, MessageCircle, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/Button';
import { Card, CardBody } from './ui/Card';
import { SkeletonCard } from './ui/Skeleton';
import { buildPendingClassesWhatsAppUrl } from '../utils/strengthStatusShare';
import { openExternalUrl } from '../utils/openExternalUrl';
import { MdmsReminderBanner, useMdmsPhaseLive } from './MdmsReminderBanner';

export function StrengthStatusPanel({
  labels,
  strengthStatus,
  isLoading,
  schoolName = '',
  useMalayalam = false,
  showMdmsReminder = true,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [updatedOpen, setUpdatedOpen] = useState(false);

  const total = strengthStatus?.length || 0;
  const updated = (strengthStatus || []).filter((c) => c.isUpdated);
  const pending = (strengthStatus || []).filter((c) => !c.isUpdated);
  const pct = total > 0 ? Math.round((updated.length / total) * 100) : 0;
  const mdmsPhase = useMdmsPhaseLive();
  const showMdmsLine =
    showMdmsReminder && mdmsPhase !== 'none' && pending.length > 0;
  const mdmsMessage =
    mdmsPhase === 'final'
      ? labels.strengthStatusMdmsFinal.replace('{count}', String(pending.length))
      : labels.strengthStatusMdmsWarning.replace('{count}', String(pending.length));

  const sharePending = () => {
    if (!pending.length) return;
    openExternalUrl(
      buildPendingClassesWhatsAppUrl(pending, { schoolName, useMalayalam })
    );
  };

  if (isLoading) {
    return (
      <Card className="border-border/80">
        <CardBody className="py-3">
          <SkeletonCard />
        </CardBody>
      </Card>
    );
  }

  if (total === 0) return null;

  if (pending.length === 0) {
    return (
      <Card className="border-success/30 bg-success/5">
        <CardBody className="flex items-center gap-2 py-3 text-sm">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden />
          <span className="text-text">
            {labels.strengthStatusAllDone.replace('{count}', String(total))}
          </span>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="border-border/80">
      <CardBody className="space-y-3 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium text-text">{labels.strengthStatusTitle}</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={sharePending}>
              <MessageCircle className="h-4 w-4" />
              {labels.strengthStatusWhatsAppShort}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDetailsOpen((v) => !v)}
              aria-expanded={detailsOpen}
            >
              {detailsOpen ? labels.strengthStatusHideDetails : labels.strengthStatusShowDetails}
              {detailsOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted">
            <span>
              {labels.strengthStatusCompact
                .replace('{updated}', String(updated.length))
                .replace('{total}', String(total))}
            </span>
            <span className="font-medium text-warning tabular-nums">
              {labels.strengthStatusPendingCount}: {pending.length}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-surface">
            <div
              className="h-full rounded-full bg-success transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {showMdmsLine && <MdmsReminderBanner phase={mdmsPhase} message={mdmsMessage} />}

        {detailsOpen && (
          <div className="space-y-2 border-t border-border pt-3">
            <p className="text-xs font-medium text-warning">
              {labels.strengthStatusPendingList} ({pending.length})
            </p>
            <ul className="max-h-48 space-y-1 overflow-y-auto overscroll-contain">
              {pending.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-2 rounded-md bg-surface/80 px-2.5 py-1.5 text-sm"
                >
                  <span className="font-medium text-text">{c.division_name}</span>
                  <span className="truncate text-xs text-muted">
                    {c.teacher_name || labels.notAssigned}
                  </span>
                </li>
              ))}
            </ul>

            {updated.length > 0 && (
              <div className="pt-1">
                <button
                  type="button"
                  onClick={() => setUpdatedOpen((v) => !v)}
                  className="text-xs font-medium text-muted hover:text-text"
                >
                  {updatedOpen
                    ? labels.strengthStatusHideUpdated
                    : labels.strengthStatusShowUpdated.replace('{count}', String(updated.length))}
                </button>
                {updatedOpen && (
                  <ul className="mt-2 max-h-36 space-y-1 overflow-y-auto overscroll-contain">
                    {updated.map((c) => (
                      <li
                        key={c.id}
                        className="flex items-center justify-between gap-2 rounded-md px-2.5 py-1 text-sm text-muted"
                      >
                        <span>{c.division_name}</span>
                        <span className="truncate text-xs">{c.teacher_name || labels.notAssigned}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
